 import React, { createContext, useContext, useReducer, useCallback, useEffect, useState, useRef, useMemo } from 'react';
import { ethers } from 'ethers';
import BeesContract from '../helpers/BeesContract';
import BearsContract from '../helpers/BearsContract';
import HoneyFarmQueenContract from '../helpers/HoneyFarmQueenContract';
import fetchData from '../helpers/fetchMetadata';
import HivesContract from '../helpers/HivesContract';
import HoneycombsContract from '../helpers/HoneycombsContract';
import BeeKeeperContract from '../helpers/BeeKeeperContract';
import { BEAR_STATUS } from '../components/honeyFarm/bearHoney';

import config from '../config';

interface HoneyFarmState {
  bears: Array<any>;
  bees: Array<any>;
  hives: Array<any>;
  honeycombs: Array<any>;
  bearHoney: number;
  beeHoney: number;
  isLoading: {
    bears: boolean;
    hives: boolean;
    honeycombs: boolean;
    bees: boolean;
  };
  selectedBears: number[];
  selectedBees: number[];
  selectedHives?: number[];
}

interface HoneyFarmContextType extends HoneyFarmState {
  refreshBearStates: () => Promise<void>;
  refreshBeeStates: () => Promise<void>;
  refreshHiveStates: () => Promise<void>;
  refreshHoneycombStates: () => Promise<void>;
  selectBear: (id: number) => void;
  selectBee: (id: number) => void;
  clearSelectedBears: () => void;
  clearSelectedBees: () => void;
  checkHiveStatus: (bearId: number) => Promise<void>;
  mintBeeFromHive?: (hiveId: number) => Promise<{ beeId?: number } | void>;
  reactivateHive?: (hiveId: number) => Promise<void>;
  // claimHoneyComb already declared below, remove duplicate
  contracts: {
    bears?: BearsContract;
    bees?: BeesContract;
    honeyFarmQueen?: HoneyFarmQueenContract;
    hives?: HivesContract;
    honeycombs?: HoneycombsContract;
    beekeeper?: BeeKeeperContract;
  };
  // New honeycomb/bee leveling actions
  claimHoneyComb?: (hiveIds: number[], quantities: number[], rarity: number, leafs: string[], merkleProofs: string[][]) => Promise<void>;
  levelUpBee?: (beeId: number, honeyCombIds: number[], quantities: number[]) => Promise<void>;
  getBeeLevel?: (beeId: number) => Promise<number>;
  loadResource: (key: ResourceKey) => Promise<void>;
  loaded: Record<ResourceKey, boolean>;
}

type ResourceKey = 'bears' | 'bees' | 'hives' | 'honeycombs';

const initialState: HoneyFarmState = {
  bears: [],
  bees: [],
  hives: [],
  honeycombs: [],
  bearHoney: 0,
  beeHoney: 0,
  isLoading: {
    bears: false,
    hives: false,
    honeycombs: false,
    bees: false,
  },
  selectedBears: [],
  selectedBees: [],
};

const initialLoadState: Record<ResourceKey, boolean> = {
  bears: false,
  bees: false,
  hives: false,
  honeycombs: false,
};

type Action =
  | { type: 'SET_BEARS'; payload: any[] }
  | { type: 'SET_BEES'; payload: any[] }
  | { type: 'SET_HIVES'; payload: any[] }
  | { type: 'SET_HONEYCOMBS'; payload: any[] }
  | { type: 'SET_BEAR_HONEY'; payload: number }
  | { type: 'SET_BEE_HONEY'; payload: number }
  | { type: 'SET_LOADING'; payload: { type: 'bears' | 'bees' | 'hives' | 'honeycombs'; value: boolean } }
  | { type: 'SELECT_BEAR'; payload: number }
  | { type: 'SELECT_BEE'; payload: number }
  | { type: 'CLEAR_SELECTED_BEARS' }
  | { type: 'CLEAR_SELECTED_BEES' };

function reducer(state: HoneyFarmState, action: Action): HoneyFarmState {
  switch (action.type) {
    case 'SET_BEARS':
      return { ...state, bears: action.payload };
    case 'SET_BEES':
      return { ...state, bees: action.payload };
    case 'SET_HIVES':
      return { ...state, hives: action.payload };
    case 'SET_HONEYCOMBS':
      return { ...state, honeycombs: action.payload };
    case 'SET_BEAR_HONEY':
      return { ...state, bearHoney: action.payload };
    case 'SET_BEE_HONEY':
      return { ...state, beeHoney: action.payload };
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: {
          ...state.isLoading,
          [action.payload.type]: action.payload.value,
        },
      };
    case 'SELECT_BEAR':
      return {
        ...state,
        selectedBears: state.selectedBears.includes(action.payload)
          ? state.selectedBears.filter(id => id !== action.payload)
          : [...state.selectedBears, action.payload],
      };
    case 'SELECT_BEE':
      return {
        ...state,
        selectedBees: state.selectedBees.includes(action.payload)
          ? state.selectedBees.filter(id => id !== action.payload)
          : [...state.selectedBees, action.payload],
      };
    case 'CLEAR_SELECTED_BEARS':
      return { ...state, selectedBears: [] };
    case 'CLEAR_SELECTED_BEES':
      return { ...state, selectedBees: [] };
    default:
      return state;
  }
}

const HoneyFarmContext = createContext<HoneyFarmContextType | undefined>(undefined);

export function HoneyFarmProvider({ 
  children,
  signer,
  provider,
  address 
}: { 
  children: React.ReactNode;
  signer?: ethers.Signer;
  provider?: ethers.providers.Provider;
  address?: string;
}) {
  const [state, dispatch] = useReducer(reducer, initialState);
  const [contracts, setContracts] = React.useState<HoneyFarmContextType['contracts']>({});
  // Read contracts use direct RPC to bypass MetaMask rate limiting
  const [readContracts, setReadContracts] = React.useState<HoneyFarmContextType['contracts']>({});
  const [hasLoaded, setHasLoaded] = useState<Record<ResourceKey, boolean>>(() => ({ ...initialLoadState }));
  const loadPromisesRef = useRef<Record<ResourceKey, Promise<void> | null>>({
    bears: null,
    bees: null,
    hives: null,
    honeycombs: null,
  });

  // Create a read-only provider that bypasses MetaMask
  const readProvider = useMemo(() => {
    return new ethers.providers.JsonRpcProvider(config.infuraUrl);
  }, []);

  // Initialize read contracts (for data fetching - bypasses MetaMask)
  useEffect(() => {
    const bearsContract = new BearsContract(readProvider);
    const beesContract = new BeesContract(readProvider);
    const honeyFarmQueen = new HoneyFarmQueenContract(readProvider);
    const hiveContract = new HivesContract(readProvider);
    const honeycombsContract = new HoneycombsContract(readProvider);
    const beekeeper = new BeeKeeperContract(readProvider);
    setReadContracts({ bears: bearsContract, bees: beesContract, honeyFarmQueen, hives: hiveContract, honeycombs: honeycombsContract, beekeeper });
  }, [readProvider]);

  // Initialize write contracts (for transactions - uses signer)
  useEffect(() => {
    const connection = signer ?? provider;
    if (connection) {
      const bearsContract = new BearsContract(connection);
      const beesContract = new BeesContract(connection);
      const honeyFarmQueen = new HoneyFarmQueenContract(connection);
      const hiveContract = new HivesContract(connection);
      const honeycombsContract = new HoneycombsContract(connection);
      const beekeeper = new BeeKeeperContract(connection);
      setContracts({ bears: bearsContract, bees: beesContract, honeyFarmQueen, hives: hiveContract, honeycombs: honeycombsContract, beekeeper });
    } else {
      setContracts({});
    }
  }, [signer, provider]);

  const resetHoneyFarmData = useCallback(() => {
    dispatch({ type: 'SET_BEARS', payload: [] });
    dispatch({ type: 'SET_BEES', payload: [] });
    dispatch({ type: 'SET_HIVES', payload: [] });
    dispatch({ type: 'SET_HONEYCOMBS', payload: [] });
    dispatch({ type: 'SET_BEAR_HONEY', payload: 0 });
    dispatch({ type: 'SET_BEE_HONEY', payload: 0 });
    dispatch({ type: 'CLEAR_SELECTED_BEARS' });
    dispatch({ type: 'CLEAR_SELECTED_BEES' });
  }, [dispatch]);

  useEffect(() => {
    setHasLoaded({ ...initialLoadState });
    loadPromisesRef.current = {
      bears: null,
      bees: null,
      hives: null,
      honeycombs: null,
    };
    resetHoneyFarmData();
  }, [address, resetHoneyFarmData]);

  const markResourceLoaded = useCallback((key: ResourceKey) => {
    setHasLoaded(prev => (prev[key] ? prev : { ...prev, [key]: true }));
  }, []);

  const refreshBearStates = useCallback(async () => {
    if (!readContracts.bears || !readContracts.honeyFarmQueen || !readContracts.hives || !address) return;

    dispatch({ type: 'SET_LOADING', payload: { type: 'bears', value: true } });
    try {
      const tokenIds: number[] = await readContracts.bears.tokensOfOwner(address).then(ids => ids.map(id => id.toNumber()));
      const bearData = await Promise.all(tokenIds.map(async (id) => {
        const honey = await readContracts.honeyFarmQueen!.getUnclaimedHoneyForBear(id);
        
        // Check hive status
        let status = BEAR_STATUS.UNKNOWN;
        try {
          const hiveExists = await readContracts.hives!.exists(id);
          status = hiveExists === true ? BEAR_STATUS.INELIGIBLE : BEAR_STATUS.CLAIMABLE;
        } catch (err) {
          console.error(`Error checking hive for bear ${id}:`, err);
        }
        
        const metaRes = await fetchData(id, 'bears'); 
        const meta = await metaRes.json();
        
        return {
          id,
          image: meta.image,
          honey,
          status,
        };
      }));

      dispatch({ type: 'SET_BEARS', payload: bearData });
      const totalHoney = await readContracts.honeyFarmQueen.getCurrentHoneyForBears(address);
      dispatch({ type: 'SET_BEAR_HONEY', payload: totalHoney });
      markResourceLoaded('bears');
    } catch (error) {
      console.error('Error fetching bear states:', error);
    } finally {
      dispatch({ type: 'SET_LOADING', payload: { type: 'bears', value: false } });
    }
  }, [readContracts.bears, readContracts.honeyFarmQueen, readContracts.hives, address, markResourceLoaded]);

  // Check hive status for a specific bear (on-demand to avoid rate limiting)
  const checkHiveStatus = useCallback(async (bearId: number) => {
    if (!readContracts.hives) return;
    
    try {
      const hiveExists = await readContracts.hives.exists(bearId);
      const newStatus = hiveExists === true ? BEAR_STATUS.INELIGIBLE : BEAR_STATUS.CLAIMABLE;
      
      // Update just this bear's status in state
      dispatch({ 
        type: 'SET_BEARS', 
        payload: state.bears.map(bear => 
          bear.id === bearId ? { ...bear, status: newStatus } : bear
        )
      });
    } catch (err) {
      console.error(`Error checking hive existence for bear ${bearId}:`, err);
      throw err;
    }
  }, [readContracts.hives, state.bears]);

  const refreshBeeStates = useCallback(async () => {
    if (!readContracts.bees || !readContracts.honeyFarmQueen || !address) return;

    dispatch({ type: 'SET_LOADING', payload: { type: 'bees', value: true } });
    try {
      const tokenIds: number[] = await readContracts.bees.tokensOfOwner(address).then(ids => ids.map(id => id.toNumber()));

      const beeData = await Promise.all(tokenIds.map(async (id) => {
        const beeState = await readContracts.honeyFarmQueen!.getBeeState(id);
        const beeLevel = await readContracts.honeyFarmQueen!.getBeeLevel(id);
        const metaRes = await fetchData(id, 'bees');
        const meta = await metaRes.json();
        
        // Calculate per-bee honey using state and level
        const honey = readContracts.honeyFarmQueen!.getUnclaimedHoneyForBee(id, beeState, beeLevel);

        return {
          id,
          image: meta.image,
          honey,
          active: Number(beeState.active),
          staked: Number(beeState.staked),
          lastTimeFed: Number(beeState.lastTimeFed),
          epochsLeft: Number(beeState.epochsLeft),
          lastRewardTime: Number(beeState.lastRewardTime),
          level: beeLevel,
        };
      }));

      dispatch({ type: 'SET_BEES', payload: beeData });
      
      // Calculate total honey from all bees
      const totalHoney = beeData.reduce((sum, bee) => sum + bee.honey, 0);
      dispatch({ type: 'SET_BEE_HONEY', payload: totalHoney });
      
      markResourceLoaded('bees');
    } catch (error) {
      console.error('Error fetching bee states:', error);
    } finally {
      dispatch({ type: 'SET_LOADING', payload: { type: 'bees', value: false } });
    }
  }, [readContracts.bees, readContracts.honeyFarmQueen, address, markResourceLoaded]);

  const refreshHiveStates = useCallback(async () => {
    if (!readContracts.hives || !readContracts.honeyFarmQueen || !address) return;

    dispatch({ type: 'SET_LOADING', payload: { type: 'hives', value: true } });
    try {
      const tokenIds: number[] = await readContracts.hives.tokensOfOwner(address).then(ids => ids.map(id => id.toNumber()));

      const hiveData = await Promise.all(tokenIds.map(async (id) => {
        // prefer typed wrapper methods on HivesContract
        let usage = 0;
        try {
          usage = await readContracts.hives!.getUsageOfMintingBee(id);
        } catch (err) {
          usage = 0;
        }
        let eligible = false;
        try {
          eligible = await readContracts.hives!.eligibleToMint(id);
        } catch (err) {
          eligible = false;
        }
        const metaRes = await fetchData(id, 'hives');
        const meta = await metaRes.json();

        return {
          id,
          image: meta.image,
          usage,
          eligible,
        };
      }));

      dispatch({ type: 'SET_HIVES', payload: hiveData });
      markResourceLoaded('hives');
    } catch (error) {
      console.error('Error fetching hive states:', error);
    } finally {
      dispatch({ type: 'SET_LOADING', payload: { type: 'hives', value: false } });
    }
  }, [readContracts.hives, readContracts.honeyFarmQueen, address, markResourceLoaded]);

  const refreshHoneycombStates = useCallback(async () => {
    if (!readContracts.honeycombs || !readContracts.beekeeper || !readContracts.hives || !address) return;
    const beekeeper = readContracts.beekeeper;

    dispatch({ type: 'SET_LOADING', payload: { type: 'honeycombs', value: true } });
    try {
      const hiveIds: number[] = await readContracts.hives.tokensOfOwner(address).then(ids => ids.map((id: any) => id.toNumber()));

      const honeycombData = await Promise.all(hiveIds.map(async (hiveId) => {
        // BeeKeeper exposes claimed counts per hive; fall back to raw contract if wrapper differs
        const claimed = await beekeeper.getClaimedCombPerHive(hiveId).catch(() => {
          return beekeeper.contract.getClaimedCombsPerHive?.(hiveId).catch(() => 0);
        });
        return {
          hiveId,
          claimed: claimed ?? 0,
        };
      }));

      dispatch({ type: 'SET_HONEYCOMBS', payload: honeycombData });
      markResourceLoaded('honeycombs');
    } catch (error) {
      console.error('Error fetching honeycomb states:', error);
    } finally {
      dispatch({ type: 'SET_LOADING', payload: { type: 'honeycombs', value: false } });
    }
  }, [readContracts.honeycombs, readContracts.beekeeper, readContracts.hives, address, markResourceLoaded]);

  const selectBear = useCallback((id: number) => {
    dispatch({ type: 'SELECT_BEAR', payload: id });
  }, []);

  const selectBee = useCallback((id: number) => {
    dispatch({ type: 'SELECT_BEE', payload: id });
  }, []);

  const clearSelectedBears = useCallback(() => {
    dispatch({ type: 'CLEAR_SELECTED_BEARS' });
  }, []);

  const clearSelectedBees = useCallback(() => {
    dispatch({ type: 'CLEAR_SELECTED_BEES' });
  }, []);

  const mintBeeFromHive = useCallback(async (hiveId: number): Promise<{ beeId?: number } | void> => {
    if (!contracts.honeyFarmQueen || !contracts.bees) return;

    dispatch({ type: 'SET_LOADING', payload: { type: 'hives', value: true } });
    try {
      // Get bee supply before minting to determine the new bee's ID
      const supplyBefore = await contracts.bees.contract.totalSupply();
      
      // Prefer wrapper method that accepts a hiveId when available
      let tx;
      if (contracts.honeyFarmQueen && (contracts.honeyFarmQueen as any).mintBeeWithHive) {
        tx = await (contracts.honeyFarmQueen as any).mintBeeWithHive(hiveId).catch(async () => {
          return await contracts.honeyFarmQueen!.mintBee();
        });
      } else {
        tx = await contracts.honeyFarmQueen.mintBee();
      }
      
      if (tx && tx.wait) {
        const receipt = await tx.wait();
        
        // Try to get the new bee ID from the event logs
        let newBeeId: number | undefined;
        
        // Look for Transfer event (typical ERC721 mint event)
        const transferEvent = receipt.events?.find((e: any) => e.event === 'Transfer');
        if (transferEvent?.args?.tokenId) {
          newBeeId = transferEvent.args.tokenId.toNumber();
        } else {
          // Fallback: get supply after minting
          const supplyAfter = await contracts.bees.contract.totalSupply();
          if (supplyAfter.gt(supplyBefore)) {
            newBeeId = supplyAfter.toNumber();
          }
        }
        
        await refreshBeeStates();
        await refreshHiveStates();
        
        return { beeId: newBeeId };
      }
      
      await refreshBeeStates();
      await refreshHiveStates();
    } catch (error) {
      console.error('Error minting bee from hive:', error);
      throw error;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: { type: 'hives', value: false } });
    }
  }, [contracts.honeyFarmQueen, contracts.bees, refreshBeeStates, refreshHiveStates]);

  const reactivateHive = useCallback(async (hiveId: number) => {
    if (!contracts.honeyFarmQueen) return;

    dispatch({ type: 'SET_LOADING', payload: { type: 'hives', value: true } });
    try {
      const tx = await contracts.honeyFarmQueen.activateHive(hiveId);
      if (tx && tx.wait) await tx.wait();
      await refreshHiveStates();
    } catch (error) {
      console.error('Error reactivating hive:', error);
      throw error;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: { type: 'hives', value: false } });
    }
  }, [contracts.honeyFarmQueen, refreshHiveStates]);

  const claimHoneyComb = useCallback(async (hiveIds: number[], quantities: number[], rarity: number, leafs: string[], merkleProofs: string[][]) => {
    if (!contracts.beekeeper) return;

    dispatch({ type: 'SET_LOADING', payload: { type: 'honeycombs', value: true } });
    try {
      const tx = await contracts.beekeeper.claimHoneyComb(hiveIds, quantities, rarity, leafs, merkleProofs);
      if (tx && tx.wait) await tx.wait();
      await refreshHoneycombStates();
    } catch (error) {
      console.error('Error claiming honeycomb:', error);
    } finally {
      dispatch({ type: 'SET_LOADING', payload: { type: 'honeycombs', value: false } });
    }
  }, [contracts.beekeeper, refreshHoneycombStates]);

  // Bee leveling logic: burn honeycomb to level up bee
  const levelUpBee = useCallback(async (beeId: number, honeyCombIds: number[], quantities: number[]) => {
    if (!contracts.honeyFarmQueen) return;
    dispatch({ type: 'SET_LOADING', payload: { type: 'honeycombs', value: true } });
    try {
      const tx = await contracts.honeyFarmQueen.levelUpBee(beeId, honeyCombIds, quantities);
      if (tx && tx.wait) await tx.wait();
      await refreshBeeStates();
      await refreshHoneycombStates();
    } catch (error) {
      console.error('Error leveling up bee:', error);
      throw error;
    } finally {
      dispatch({ type: 'SET_LOADING', payload: { type: 'honeycombs', value: false } });
    }
  }, [contracts.honeyFarmQueen, refreshBeeStates, refreshHoneycombStates]);

  const getBeeLevel = useCallback(async (beeId: number) => {
    if (!readContracts.honeyFarmQueen) return -1;
    try {
      return await readContracts.honeyFarmQueen.getBeeLevel(beeId);
    } catch (error) {
      console.error('Error fetching bee level:', error);
      return -1;
    }
  }, [readContracts.honeyFarmQueen]);

  const refreshByKey = useCallback((key: ResourceKey) => {
    switch (key) {
      case 'bears':
        return refreshBearStates();
      case 'bees':
        return refreshBeeStates();
      case 'hives':
        return refreshHiveStates();
      case 'honeycombs':
        return refreshHoneycombStates();
      default:
        return Promise.resolve();
    }
  }, [refreshBearStates, refreshBeeStates, refreshHiveStates, refreshHoneycombStates]);

  const loadResource = useCallback(async (key: ResourceKey) => {
    if (!address) return;
    if (hasLoaded[key]) return;

    if (!loadPromisesRef.current[key]) {
      loadPromisesRef.current[key] = refreshByKey(key).finally(() => {
        loadPromisesRef.current[key] = null;
      });
    }

    return loadPromisesRef.current[key] ?? undefined;
  }, [address, hasLoaded, refreshByKey]);

  const value: HoneyFarmContextType = {
    ...state,
    refreshBearStates,
    refreshBeeStates,
    refreshHiveStates,
    refreshHoneycombStates,
    selectBear,
    selectBee,
    clearSelectedBears,
    clearSelectedBees,
    checkHiveStatus,
    mintBeeFromHive,
    reactivateHive,
    claimHoneyComb,
    levelUpBee,
    getBeeLevel,
    contracts,
    loadResource,
    loaded: hasLoaded,
  };

  return (
    <HoneyFarmContext.Provider value={value}>
      {children}
    </HoneyFarmContext.Provider>
  );
}

export function useHoneyFarm() {
  const context = useContext(HoneyFarmContext);
  if (context === undefined) {
    throw new Error('useHoneyFarm must be used within a HoneyFarmProvider');
  }
  return context;
}

export function useBearHoney() {
  const {
    bears,
    bearHoney,
    selectedBears,
    isLoading,
    refreshBearStates,
    selectBear,
    clearSelectedBears,
    checkHiveStatus,
    contracts,
    loadResource,
    loaded,
  } = useHoneyFarm();

  useEffect(() => {
    loadResource('bears').catch((error: unknown) => console.error('Failed to load bear data', error));
  }, [loadResource]);

  return {
    bears,
    honey: bearHoney,
    selected: selectedBears,
    loading: isLoading.bears,
    refreshStates: refreshBearStates,
    select: selectBear,
    clearSelected: clearSelectedBears,
    checkHiveStatus,
    honeyFarmQueen: contracts.honeyFarmQueen,
    loaded: loaded.bears,
  };
}

export function useBeeHoney() {
  const {
    bees,
    beeHoney,
    selectedBees,
    isLoading,
    refreshBeeStates,
    selectBee,
    clearSelectedBees,
    contracts,
    loadResource,
    loaded,
  } = useHoneyFarm();

  useEffect(() => {
    loadResource('bees').catch((error: unknown) => console.error('Failed to load bee data', error));
  }, [loadResource]);

  return {
    bees,
    honey: beeHoney,
    selected: selectedBees,
    loading: isLoading.bees,
    refreshStates: refreshBeeStates,
    select: selectBee,
    clearSelected: clearSelectedBees,
    honeyFarmQueen: contracts.honeyFarmQueen,
    loaded: loaded.bees,
  };
}

export function useHiveHoney() {
  const {
    hives,
    isLoading,
    refreshHiveStates,
    contracts,
    mintBeeFromHive,
    reactivateHive,
    loadResource,
    loaded,
    // selected hives not currently tracked
  } = useHoneyFarm();

  useEffect(() => {
    loadResource('hives').catch((error: unknown) => console.error('Failed to load hive data', error));
  }, [loadResource]);

  return {
    hives,
    loading: isLoading.hives,
    refreshStates: refreshHiveStates,
    mintBeeFromHive,
    reactivateHive,
    honeyFarmQueen: contracts.honeyFarmQueen,
    hivesContract: contracts.hives,
    loaded: loaded.hives,
  };
}

export function useHoneycombs() {
  const {
    honeycombs,
    isLoading,
    refreshHoneycombStates,
    contracts,
    claimHoneyComb,
    loadResource,
    loaded,
  } = useHoneyFarm();

  useEffect(() => {
    loadResource('honeycombs').catch((error: unknown) => console.error('Failed to load honeycomb data', error));
  }, [loadResource]);

  return {
    honeycombs,
    loading: isLoading.honeycombs,
    refreshStates: refreshHoneycombStates,
    claimHoneyComb,
    beekeeper: contracts.beekeeper,
    loaded: loaded.honeycombs,
  };
}